<template>
  <div>
    <!-- Filters row -->
    <div class="doc-filters">
      <div class="doc-tabs">
        <button
          v-for="filter in docFilters"
          :key="filter.key"
          type="button"
          class="dir-tab"
          :class="{ active: activeFilter === filter.key }"
          @click="
            activeFilter = filter.key;
            visibleCount = pageSize;
          "
          style="font-size: 12px; padding: 5px 12px"
        >
          {{ filter.label }}
          <span v-if="getCount(filter.key)" class="doc-tab-count">{{
            getCount(filter.key)
          }}</span>
        </button>
      </div>
      <el-select
        v-model="yearFilter"
        placeholder="Año"
        size="mini"
        style="width: 110px"
        @change="visibleCount = pageSize"
      >
        <el-option label="Todos" :value="''" />
        <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y" />
      </el-select>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="dir-panel-empty">
      <i
        class="el-icon-loading"
        style="font-size: 28px; margin-bottom: 8px; display: block"
      ></i>
      Cargando documentos...
    </div>

    <!-- Document list -->
    <div v-else-if="filteredDocuments.length">
      <div
        v-for="(doc, index) in paginatedDocuments"
        :key="doc.type + '-' + doc.id + '-' + index"
        class="dir-doc-row"
        @click="navigateToDoc(doc)"
      >
        <span
          class="dir-doc-dot"
          :style="{ background: getDocColor(doc.type) }"
        ></span>
        <div class="dir-doc-info">
          <div class="dir-doc-number-row">
            <span class="dir-doc-number">{{ doc.number }}</span>
            <span v-if="doc.title" class="dir-doc-title">{{ doc.title }}</span>
          </div>
          <span class="dir-doc-name">{{ doc.name }}</span>
        </div>
        <div class="dir-doc-right">
          <span class="dir-doc-amount">${{ formatAmount(doc.total) }}</span>
          <span class="dir-doc-date">{{ formatDate(doc.date) }}</span>
        </div>
        <span
          v-if="doc.statusLabel"
          class="dir-doc-status"
          :class="'dir-doc-status--' + doc.statusClass"
        >
          {{ doc.statusLabel }}
        </span>
      </div>

      <!-- Load more button -->
      <div v-if="hasMore" class="dir-doc-loadmore">
        <button type="button" class="dir-doc-loadmore-btn" @click="loadMore">
          Ver más ({{ remainingCount }})
        </button>
      </div>

      <!-- Total -->
      <div class="dir-doc-total">
        {{ filteredDocuments.length }} documentos &middot;
        <strong>${{ formatAmount(visibleTotal) }}</strong>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="dir-panel-empty">
      <i
        class="el-icon-document"
        style="font-size: 32px; margin-bottom: 8px; display: block"
      ></i>
      No hay documentos registrados
    </div>
  </div>
</template>

<script>
import invoiceService from "../../sales/pages/invoices/services/invoiceService";
import quoteService from "../../sales/pages/quotes/services/quoteService";
import creditNoteService from "../../sales/pages/credit-note/services/creditNoteService";
import debitNoteService from "../../sales/pages/debit-note/services/debitNoteService";

const currentYear = new Date().getFullYear();
const PAGE_SIZE = 20;

export default {
  name: "DocumentsTab",
  props: {
    businessId: { type: [Number, String], required: true }
  },
  data() {
    return {
      activeFilter: "invoice",
      yearFilter: "",
      documents: [],
      loading: true,
      visibleCount: PAGE_SIZE,
      pageSize: PAGE_SIZE
    };
  },
  computed: {
    docFilters() {
      return [
        { key: "quote", label: "Cotizaciones" },
        { key: "invoice", label: "Facturas" },
        { key: "credit_note", label: "N. Crédito" },
        { key: "debit_note", label: "N. Débito" }
      ];
    },
    yearOptions() {
      const yearsSet = new Set();
      this.documents.forEach(d => {
        if (d.date) {
          yearsSet.add(new Date(d.date).getFullYear());
        }
      });
      yearsSet.add(currentYear);
      return Array.from(yearsSet).sort((a, b) => b - a);
    },
    filteredDocuments() {
      let docs = this.documents.filter(d => d.type === this.activeFilter);
      if (this.yearFilter) {
        docs = docs.filter(d => {
          const docDate = new Date(d.date);
          return docDate.getFullYear() === this.yearFilter;
        });
      }
      return docs;
    },
    paginatedDocuments() {
      return this.filteredDocuments.slice(0, this.visibleCount);
    },
    hasMore() {
      return this.visibleCount < this.filteredDocuments.length;
    },
    remainingCount() {
      return this.filteredDocuments.length - this.visibleCount;
    },
    visibleTotal() {
      return this.filteredDocuments.reduce(
        (sum, d) => sum + (Number(d.total) || 0),
        0
      );
    }
  },
  methods: {
    loadMore() {
      this.visibleCount += this.pageSize;
    },
    getCount(type) {
      let docs = this.documents.filter(d => d.type === type);
      if (this.yearFilter) {
        docs = docs.filter(d => {
          const docDate = new Date(d.date);
          return docDate.getFullYear() === this.yearFilter;
        });
      }
      return docs.length;
    },
    getDocColor(type) {
      const colors = {
        invoice: "#3fdbbf",
        quote: "#31355c",
        credit_note: "#fcd70d",
        debit_note: "#f56c6c"
      };
      return colors[type] || "#989aae";
    },
    getStatusInfo(type, raw) {
      if (!raw && raw !== 0) return { label: "", cls: "" };
      if (type === "invoice") {
        const map = {
          0: { label: "Borrador", cls: "draft" },
          1: { label: "Emitida", cls: "active" },
          2: { label: "Anulada", cls: "void" },
          3: { label: "Pagada", cls: "paid" },
          draft: { label: "Borrador", cls: "draft" },
          success: { label: "Emitida", cls: "active" },
          nuled: { label: "Anulada", cls: "void" },
          paid: { label: "Pagada", cls: "paid" }
        };
        return map[raw] || { label: "", cls: "" };
      }
      if (type === "quote") {
        const map = {
          0: { label: "Borrador", cls: "draft" },
          1: { label: "Enviada", cls: "active" },
          2: { label: "Aceptada", cls: "paid" },
          3: { label: "Rechazada", cls: "void" },
          draft: { label: "Borrador", cls: "draft" },
          sent: { label: "Enviada", cls: "active" },
          accepted: { label: "Aceptada", cls: "paid" },
          rejected: { label: "Rechazada", cls: "void" }
        };
        return map[raw] || { label: "", cls: "" };
      }
      if (type === "credit_note" || type === "debit_note") {
        const map = {
          draft: { label: "Borrador", cls: "draft" },
          success: { label: "Autorizada", cls: "paid" },
          nuled: { label: "Anulada", cls: "void" }
        };
        return map[raw] || { label: "", cls: "" };
      }
      return { label: "", cls: "" };
    },
    formatAmount(value) {
      if (!value) return "0.00";
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const months = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic"
      ];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    navigateToDoc(doc) {
      if (!doc.id) return;
      if (doc.type === "invoice") {
        this.$root.$emit("preview-invoice", doc.id);
        return;
      }
      if (doc.type === "quote") {
        this.$root.$emit("preview-quote", doc.id);
        return;
      }
      // Credit / debit notes — navigate to view (read-only)
      const routes = {
        credit_note: "sales.creditNote.view",
        debit_note: "sales.debitNote.view"
      };
      const routeName = routes[doc.type];
      if (routeName) {
        this.$router
          .push({ name: routeName, params: { id: doc.id } })
          .catch(() => {});
      }
    },
    selectDefaultTab() {
      // Auto-select the first tab that has documents
      const priority = ["invoice", "quote", "credit_note", "debit_note"];
      for (const type of priority) {
        if (this.documents.some(d => d.type === type)) {
          this.activeFilter = type;
          return;
        }
      }
      this.activeFilter = "invoice";
    },
    async loadDocuments() {
      this.loading = true;
      this.documents = [];

      const clientId = this.businessId;

      const [
        invoicesRes,
        quotesRes,
        creditNotesRes,
        debitNotesRes
      ] = await Promise.allSettled([
        invoiceService.getClientInvoices(clientId),
        quoteService.getClientQuotes(clientId),
        creditNoteService.getCreditNotes({ params: { client: clientId } }),
        debitNoteService.getDebitNotes({ params: { client: clientId } })
      ]);

      // Invoices
      const invoicesArr =
        invoicesRes.status === "fulfilled" && invoicesRes.value.data
          ? invoicesRes.value.data.invoices ||
            (Array.isArray(invoicesRes.value.data.data)
              ? invoicesRes.value.data.data
              : [])
          : [];
      if (invoicesArr.length) {
        invoicesArr.forEach(inv => {
          const status = this.getStatusInfo("invoice", inv.status);
          this.documents.push({
            type: "invoice",
            number: inv.number_invoice || inv.secuence || "—",
            name: inv.client ? inv.client.name : "",
            total: inv.total,
            date: inv.emission_date || inv.created_at,
            id: inv.id,
            statusLabel: status.label,
            statusClass: status.cls
          });
        });
      }

      // Quotes
      const quotesArr =
        quotesRes.status === "fulfilled" && quotesRes.value.data
          ? quotesRes.value.data.quotes ||
            (Array.isArray(quotesRes.value.data.data)
              ? quotesRes.value.data.data
              : [])
          : [];
      if (quotesArr.length) {
        quotesArr.forEach(q => {
          const status = this.getStatusInfo("quote", q.status);
          const lastVersion =
            q.versions && q.versions.length ? q.versions[0] : null;
          this.documents.push({
            type: "quote",
            number: q.code || q.secuence || q.number || "—",
            title:
              lastVersion && lastVersion.name ? lastVersion.name : q.name || "",
            name: q.client ? q.client.name : "",
            total: lastVersion ? lastVersion.total : q.total || 0,
            date: lastVersion ? lastVersion.created_at : q.created_at || "",
            id: q.id,
            statusLabel: status.label,
            statusClass: status.cls
          });
        });
      }

      // Credit Notes
      const creditArr =
        creditNotesRes.status === "fulfilled" && creditNotesRes.value.data
          ? creditNotesRes.value.data.credit_notes ||
            (Array.isArray(creditNotesRes.value.data.data)
              ? creditNotesRes.value.data.data
              : [])
          : [];
      if (creditArr.length) {
        creditArr.forEach(cn => {
          const status = this.getStatusInfo("credit_note", cn.status);
          this.documents.push({
            type: "credit_note",
            number:
              cn.identification_document ||
              cn.number_credit_note ||
              cn.secuence ||
              "—",
            name:
              cn.invoice && cn.invoice.client
                ? cn.invoice.client.name
                : cn.client
                ? cn.client.name
                : "",
            total: cn.total,
            date: cn.created_at,
            id: cn.id,
            statusLabel: status.label,
            statusClass: status.cls
          });
        });
      }

      // Debit Notes
      const debitArr =
        debitNotesRes.status === "fulfilled" && debitNotesRes.value.data
          ? debitNotesRes.value.data.debit_notes ||
            (Array.isArray(debitNotesRes.value.data.data)
              ? debitNotesRes.value.data.data
              : [])
          : [];
      if (debitArr.length) {
        debitArr.forEach(dn => {
          const status = this.getStatusInfo("debit_note", dn.status);
          this.documents.push({
            type: "debit_note",
            number:
              dn.identification_document ||
              dn.number_debit_note ||
              dn.secuence ||
              "—",
            name:
              dn.invoice && dn.invoice.client
                ? dn.invoice.client.name
                : dn.client
                ? dn.client.name
                : "",
            total: dn.total,
            date: dn.created_at,
            id: dn.id,
            statusLabel: status.label,
            statusClass: status.cls
          });
        });
      }

      // Sort by date descending
      this.documents.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Auto-select tab with documents
      this.selectDefaultTab();

      this.loading = false;
    }
  },
  mounted() {
    this.loadDocuments();
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../theme/variables";

.doc-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.doc-tabs {
  display: flex;
  gap: 4px;
  flex: 1;
  flex-wrap: wrap;
}
.doc-tab-count {
  background: rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 0 6px;
  font-size: 11px;
  margin-left: 4px;
}

.dir-panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 200px;
  padding: 40px 20px;
  color: $--color-text-gray;
  font-size: 14px;
}

.dir-doc-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 8px;
  border-bottom: 1px solid $--color-gray;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: $--color-bg-gray;
  }
}

.dir-doc-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dir-doc-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dir-doc-number-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.dir-doc-number {
  font-size: 13px;
  font-weight: 600;
  color: $--color-primary;
  white-space: nowrap;
  flex-shrink: 0;
}
.dir-doc-title {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.dir-doc-name {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dir-doc-right {
  text-align: right;
  flex-shrink: 0;
}

.dir-doc-amount {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: $--color-primary;
}

.dir-doc-date {
  display: block;
  font-size: 11px;
  color: #909399;
}

.dir-doc-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  white-space: nowrap;

  &--draft {
    background: #f4f5f7;
    color: #909399;
  }
  &--active {
    background: #e8f8f3;
    color: #1d9e75;
  }
  &--paid {
    background: #e8f8f3;
    color: #1d9e75;
  }
  &--void {
    background: #fff0f0;
    color: #e24b4a;
  }
}

.dir-doc-loadmore {
  display: flex;
  justify-content: center;
  padding: 12px 8px;
  border-bottom: 1px solid $--color-gray;
}

.dir-doc-loadmore-btn {
  border: none;
  background: none;
  color: $--color-primary;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: $--color-bg-gray;
  }
}

.dir-doc-total {
  text-align: right;
  padding: 12px 8px;
  font-size: 13px;
  color: #909399;

  strong {
    font-weight: 600;
    color: $--color-primary;
  }
}
</style>
